import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "components/ui/select";
import { useQueryClient } from "@tanstack/react-query";
import { useUpdateStripeCard, subscriptionKeys } from "core";
import { toast } from "components/ui/toast";

interface UpdateCardDialogProps {
  open: boolean;
  onClose: () => void;
  card?: {
    id: number;
    methodType: string;
    cardId: string; // ✅ Stripe card id
    brand: string;
    expMonth: number;
    expYear: number;
    last4Digits: string;
    zip: string;
    name: string;
  };
}

export const UpdatePaymentCard: React.FC<UpdateCardDialogProps> = ({
  open,
  onClose,
  card,
}) => {
  const queryClient = useQueryClient();
  const { mutateAsync: updateCard, isPending } = useUpdateStripeCard();

  const [form, setForm] = useState({
    expMonth: 1,
    expYear: new Date().getFullYear().toString(),
    zip: "",
    stripeTokenId: "",
  });

  useEffect(() => {
    if (card) {
      setForm({
        expMonth: card.expMonth ?? 1,
        expYear: card.expYear?.toString() ?? new Date().getFullYear().toString(),
        zip: card.zip ?? "",
        stripeTokenId: card.cardId ?? "", // ✅ Stripe expects this field name
      });
    }
  }, [card]);

  const months = [
    { value: 1, label: "Jan" },
    { value: 2, label: "Feb" },
    { value: 3, label: "Mar" },
    { value: 4, label: "Apr" },
    { value: 5, label: "May" },
    { value: 6, label: "Jun" },
    { value: 7, label: "Jul" },
    { value: 8, label: "Aug" },
    { value: 9, label: "Sep" },
    { value: 10, label: "Oct" },
    { value: 11, label: "Nov" },
    { value: 12, label: "Dec" },
  ];

  const years = Array.from({ length: 15 }, (_, i) =>
    (new Date().getFullYear() + i).toString()
  );

  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    // ✅ only numeric
    if (/^\d*$/.test(val) && val.length <= 6) {
      setForm((prev) => ({ ...prev, zip: val }));
    }
  };

  const handleSubmit = async () => {
    if (!form.stripeTokenId) {
      toast.error("Stripe card ID missing.");
      return;
    }

    if (!form.zip || form.zip.length < 4) {
      toast.error("Please enter a valid Pin Code (numbers only)");
      return;
    }

    const payload = {
      expMonth: form.expMonth,
      expYear: form.expYear,
      zip: form.zip,
      stripeTokenId: form.stripeTokenId,
    };

    try {
      await updateCard(payload);
      toast.success("Card details updated successfully!");
      await queryClient.invalidateQueries({
        queryKey: subscriptionKeys.cards(),
      });
      onClose();
    } catch (err: any) {
      const errorMessage = "Something went wrong";
      toast.error(errorMessage);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm ">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            Update Card Details
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 mt-4">
          {/* Month */}
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium w-28 shrink-0">Month</label>
            <Select
              value={String(form.expMonth)}
              onValueChange={(val) =>
                setForm((p) => ({ ...p, expMonth: Number(val) }))
              }
            >
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Select Month" />
              </SelectTrigger>
              <SelectContent>
                {months.map((m) => (
                  <SelectItem key={m.value} value={String(m.value)}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Year */}
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium w-28 shrink-0">Year</label>
            <Select
              value={form.expYear}
              onValueChange={(val) =>
                setForm((p) => ({ ...p, expYear: val }))
              }
            >
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Select Year" />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => (
                  <SelectItem key={y} value={y}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* ZIP */}
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium w-28 shrink-0">Pin Code</label>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className="flex-1"
              value={form.zip}
              onChange={handleZipChange}
              maxLength={6}
              autoFocus
              placeholder="Enter PIN"
            />
          </div>
        </div>

        <DialogFooter className="mt-6 flex justify-end space-x-2">
          <Button
            variant="secondary"
            onClick={() => onClose()}
            disabled={isPending}
            className="min-w-[100px]"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isPending || !form.zip}
            className="min-w-[140px]"
          >
            {isPending ? "Updating..." : "Update Details"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
